import { Loader2, Truck } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useShippingRatesList } from '@/lib/api/shippingRates'

import type { RuntimeStepConfig } from '../types'

interface ShippingRatesStepConfigProps {
  storeId: string
  value: RuntimeStepConfig['shippingRateSettings']
  onChange: (config: RuntimeStepConfig['shippingRateSettings']) => void
}

const buildQuery = (storeId: string) => ({
  drizzQuery: {
    select: ['id', 'shippingRateName', 'description', 'icon', 'amount', 'countryCodes', 'storeId'],
    from: 'shippingRates',
    where: [
      {
        table: 'shippingRates',
        column: 'storeId',
        operator: 'eq' as const,
        value: storeId,
      },
    ],
    orderBy: [
      {
        table: 'shippingRates',
        column: 'createdAt',
        direction: 'desc' as const,
      },
    ],
    limit: 200,
    offset: 0,
    joins: [],
  },
})

export function ShippingRatesStepConfig({ storeId, value, onChange }: ShippingRatesStepConfigProps) {
  const { t } = useTranslation()
  const query = useMemo(() => buildQuery(storeId), [storeId])
  const { data, isLoading } = useShippingRatesList(query, { enabled: !!storeId })
  const rates: Array<{
    id: string
    shippingRateName?: string
    description?: string | null
    icon?: string | null
  }> = (data?.items as any[]) ?? []

  // Hide-list semantics: listed IDs are hidden, everything else is visible.
  // New rates added later show up automatically.
  const hiddenIds = value?.hiddenShippingRateIds ?? []
  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds])

  const setHidden = (next: string[]) => {
    onChange(next.length === 0 ? undefined : { hiddenShippingRateIds: next })
  }

  const toggleVisible = (rateId: string, visible: boolean) => {
    if (visible) {
      setHidden(hiddenIds.filter((id) => id !== rateId))
    } else {
      if (hiddenSet.has(rateId)) return
      setHidden([...hiddenIds, rateId])
    }
  }

  const showAll = () => onChange(undefined)
  const hideAll = () => onChange({ hiddenShippingRateIds: rates.map((r) => r.id) })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-1 py-3 text-xs text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t('stepEditor.shippingRates.loading')}
      </div>
    )
  }

  if (rates.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-500 dark:border-slate-700">
        {t('stepEditor.shippingRates.empty')}
      </div>
    )
  }

  const visibleCount = rates.length - hiddenSet.size
  const allVisible = hiddenSet.size === 0

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span>
          {allVisible
            ? t('stepEditor.shippingRates.allVisible')
            : t('stepEditor.shippingRates.someVisible', { count: visibleCount, total: rates.length })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={showAll}
            className="text-primary hover:underline disabled:opacity-50"
            disabled={allVisible}
          >
            {t('stepEditor.shippingRates.showAll')}
          </button>
          <span className="text-slate-300">|</span>
          <button
            type="button"
            onClick={hideAll}
            className="text-primary hover:underline disabled:opacity-50"
            disabled={hiddenSet.size === rates.length}
          >
            {t('stepEditor.shippingRates.hideAll')}
          </button>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 dark:border-slate-700">
        {rates.map((rate, idx) => {
          const visible = !hiddenSet.has(rate.id)
          const inputId = `shipping-rate-${rate.id}`
          return (
            <div
              key={rate.id}
              className={
                'flex items-start gap-2.5 px-3 py-2.5 ' +
                (idx > 0 ? 'border-t border-slate-100 dark:border-slate-800' : '')
              }
            >
              <Checkbox
                id={inputId}
                checked={visible}
                onCheckedChange={(c) => toggleVisible(rate.id, c === true)}
                className="mt-0.5"
              />
              <Label htmlFor={inputId} className="flex-1 cursor-pointer space-y-0.5">
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">
                  {rate.icon ? (
                    <img
                      src={rate.icon}
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                    />
                  ) : (
                    <Truck className="h-3.5 w-3.5 text-slate-400" />
                  )}
                  {rate.shippingRateName || rate.id}
                </div>
                {rate.description && (
                  <div className="text-xs font-normal text-slate-500">{rate.description}</div>
                )}
              </Label>
            </div>
          )
        })}
      </div>
    </div>
  )
}
